import * as gtag from "../lib/gtag"
import { useFetcher } from "@today/api"
import {
  checkPassword,
  isPhoneNumberCorrect,
} from "../components/incheon/validations"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { formatRegistrationNumber } from "@today/lib"
import axios, { AxiosError } from "axios"
import { SearchCompanyErrorCode } from "../pages/api/register/search-company"
import { InputOverrides } from "baseui/input"
import { getErrorMessage } from "@today/api/utils"

export enum Step {
  InputRegistrationNumber,
  LoadingCompanyInfo,
  InputCustomCompanyName,
  ConfirmCompanyInfo,
  InputServiceName, // 서비스명
  CompanyRepresentativeName, // 대표자명
  InputAddress, // 사업장 주소
  InputPhoneNumber,
  SendingPhoneNumberVerificationKey,
  InputPhoneNumberVerificationKey,
  CheckingPhoneNumberVerificationKey,
  InputPassword,
  RepeatPassword,
  BeforeReadingTerm,
  AgreeTerm,
  AllDone,
}

export type FocusItem =
  | "registrationNumber"
  | "companyName"
  | "companyRepresentativeName"
  | "serviceName"
  | "phone"
  | "verificationCode"
  | "password"
  | "repeatingPassword"
  | "readTerm"
  | "register"

type Event =
  | "change_step"
  | "get_company_info"
  | "register_account"
  | "click_button"

type Category = "musinsa_registration"
function sendEvent(
  event: Event,
  label: string,
  registrationNumber: string,
  category: Category = "musinsa_registration"
) {
  gtag.event(
    {
      action: event,
      label,
      category,
      value: 1,
    },
    {
      ...(registrationNumber
        ? { registration_number: formatRegistrationNumber(registrationNumber) }
        : {}),
    }
  )
}

function useSignup() {
  const fetcher = useFetcher()
  const [currentStep, setCurrentStepBase] = useState<Step>(
    Step.InputRegistrationNumber
  )

  const setCurrentStep = useCallback((step: Step) => {
    setCurrentStepBase(step)
  }, [])

  // 사업자등록번호 관리
  const registrationNumberRef = useRef("")
  const [registrationNumber, setRegistrationNumber] = useState("")
  const [companyName, setCompanyName] = useState("")
  const [companyNameStatus, setCompanyNameStatus] = useState<
    "AUTOMATIC" | "CUSTOM" | "CUSTOM_CONFIRMED" | "AUTOMATIC_CONFIRMED"
  >()

  // 서비스명
  const [serviceName, setServiceName] = useState("")

  // 대표자명
  const [companyRepresentativeName, setCompanyRepresentativeName] = useState("")

  const formattedRegisterNumber = useMemo(
    () =>
      [
        registrationNumber.slice(0, 3),
        registrationNumber.slice(3, 5),
        registrationNumber.slice(5),
      ]
        .filter((token) => !!token)
        .join("-"),
    [registrationNumber]
  )

  const companyNameCacheRef = useRef(new Map<string, string>())
  const inputCompanyNameRef = useRef<HTMLInputElement>(null)
  const inputCompanyRepresentativeNameRef = useRef<HTMLInputElement>(null)
  const inputPhoneRef = useRef<HTMLInputElement>(null)
  const inputRegistrationNumberRef = useRef<HTMLInputElement>(null)
  const inputVerificationCodeRef = useRef<HTMLInputElement>(null)
  const inputPasswordRef = useRef<HTMLInputElement>(null)
  const inputRepeatPasswordRef = useRef<HTMLInputElement>(null)
  const buttonReadTermRef = useRef<HTMLButtonElement>(null)
  const buttonRegisterRef = useRef<HTMLButtonElement>(null)
  const scrollTermRef = useRef<HTMLDivElement>(null)
  const inputAddressRef = useRef<HTMLInputElement>(null)
  const inputNameRef = useRef<HTMLInputElement>(null)

  const [
    invalidRegistrationNumberErrorMessage,
    setInvalidRegistrationNumberErrorMessage,
  ] = useState<string | undefined>(undefined)

  // 인증번호 입력
  const [transactionId, setTransactionId] = useState()
  const [verificationCode, setVerificationCode] = useState("")
  const [hasPhoneNumberVerified, setHasPhoneNumberVerified] = useState(false)
  const [verificationCodeErrorMessage, setVerificationCodeErrorMessage] =
    useState<string>()

  // 비밀번호 입력
  const [password, setPassword] = useState("")
  const { isGoodPassword, passwordErrorMessage } = useMemo(
    () => checkPassword(password),
    [password]
  )
  const [hasPasswordChecked, setHasPasswordChecked] = useState(false)
  // 비밀번호 확인
  const [repeatedPassword, setRepeatedPassword] = useState("")
  const [hasRepeatedPasswordChecked, setHasRepeatedPasswordChecked] =
    useState(false)
  const [showPasswordsMismatched, setShowPasswordsMismatched] = useState(false)

  useEffect(() => {
    setShowPasswordsMismatched(false)
  }, [repeatedPassword, password])

  // 출고지 주소
  const [address, setAddress] = useState("")
  const [addressError, setAddressError] = useState("")
  const checkAddress = useCallback(async () => {
    if (!address) {
      setAddressError("주소를 입력해주세요.")
      return
    }
    if (address.length < 10) {
      setAddressError("주소가 올바르지 않습니다.")
      return
    }
    try {
      await fetcher("/api/register/check-address", { address })

      setAddressError("")
      setCurrentStep(Step.InputPhoneNumber)
    } catch (error) {
      setAddressError(getErrorMessage(error))
    }
  }, [address])

  // 약관 스크롤 관리
  const [hasAgreedTerm, setHasAgreedTerm] = useState(false)
  const [hasReachedEndOfTerm, setHasReachedEndOfTerm] = useState(false)

  const checkScrollTermIsEnd = useCallback(() => {
    if (scrollTermRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollTermRef.current
      if (scrollTop + clientHeight >= scrollHeight - 5) {
        setHasReachedEndOfTerm(true)
      }
    }
  }, [])

  useEffect(() => {
    if (currentStep < Step.AgreeTerm) return
    checkScrollTermIsEnd()

    const scrollElement = scrollTermRef.current
    if (scrollElement) {
      scrollElement.addEventListener("scroll", checkScrollTermIsEnd)
    }
    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener("scroll", checkScrollTermIsEnd)
      }
    }
  }, [currentStep])

  useEffect(() => {
    if (registrationNumber.length !== 10) return
    setInvalidRegistrationNumberErrorMessage(undefined)

    registrationNumberRef.current = registrationNumber
    const cachedCompanyName =
      companyNameCacheRef.current.get(registrationNumber)
    if (cachedCompanyName) {
      setCompanyName(cachedCompanyName)
      setCurrentStep(Step.ConfirmCompanyInfo)
      return
    }
    setCurrentStep(Step.LoadingCompanyInfo)
    ;(async () => {
      try {
        const data = await fetcher<{
          registrationNumber: string
          companyName: string
        }>(
          `/api/register/search-company?registrationNumber=${registrationNumber}`
        )
        companyNameCacheRef.current.set(
          data.registrationNumber,
          data.companyName
        )
        if (data.registrationNumber === registrationNumberRef.current) {
          setCompanyName(data.companyName)
          setCompanyNameStatus("AUTOMATIC")
          setCurrentStep(Step.ConfirmCompanyInfo)
        }
      } catch (error) {
        if (registrationNumber !== registrationNumberRef.current) {
          // 과거 데이터에서 난 에러는 무시
          return
        }
        const errorCode = (
          axios.isAxiosError(error)
            ? (error as AxiosError).response?.data?.errorCode ??
              "INTERNAL_ERROR"
            : "INTERNAL_ERROR"
        ) as SearchCompanyErrorCode
        if (errorCode === "NOT_REGISTERED_IN_NTS") {
          sendEvent(
            "get_company_info",
            "not_registered_in_nts",
            registrationNumber
          )
          setInvalidRegistrationNumberErrorMessage(
            "국세청에 등록되지 않은 사업자 등록번호입니다. 문제가 계속될 경우 고객센터로 문의주시길 바랍니다."
          )
          setCurrentStep(Step.InputRegistrationNumber)
        } else if (errorCode !== "FAIL_TO_GET_COMPANY_NAME") {
          setInvalidRegistrationNumberErrorMessage(
            getCompanyErrorMessage(errorCode)
          )
          setCurrentStep(Step.InputRegistrationNumber)
        } else {
          sendEvent(
            "get_company_info",
            "fail_to_get_company_name",
            registrationNumber
          )
          setCompanyNameStatus("CUSTOM")
          setCurrentStep(Step.InputCustomCompanyName)
        }
      }
    })()
  }, [registrationNumber])

  // 담당자 휴대전화 관리
  const [phoneNumber, setPhoneNumber] = useState("")
  const formattedPhoneNumber = useMemo(
    () =>
      [
        phoneNumber.slice(0, 3),
        phoneNumber.slice(3, phoneNumber.length === 10 ? 6 : 7),
        phoneNumber.slice(phoneNumber.length === 10 ? 6 : 7),
      ]
        .filter((token) => !!token)
        .join("-"),
    [phoneNumber]
  )

  const checkVerificationCode = useCallback(
    (verificationCode: string) => {
      setCurrentStep(Step.CheckingPhoneNumberVerificationKey)
      setVerificationCodeErrorMessage(undefined)
      ;(async () => {
        try {
          const data = await fetcher(
            "/api/register/check-phone-verification-code",
            {
              phoneNumber,
              verificationCode,
            }
          )
          if (data.transactionId) {
            setTransactionId(data.transactionId)
          }
          setCurrentStep(Step.InputPassword)
          setVerificationCode("")
          setHasPhoneNumberVerified(false)
        } catch (error) {
          const defaultErrorMessage =
            "에러가 발생하였습니다. 잠시 후 다시 시도해주세요."
          const errorMessage = axios.isAxiosError(error)
            ? (error as AxiosError).response?.data?.errorMessage ??
              defaultErrorMessage
            : defaultErrorMessage
          setVerificationCodeErrorMessage(errorMessage)
          setCurrentStep(Step.InputPhoneNumberVerificationKey)
          setHasPhoneNumberVerified(true)
        }
      })()
    },
    [phoneNumber]
  )

  const formValidation =
    registrationNumber.length === 10 &&
    !invalidRegistrationNumberErrorMessage &&
    isPhoneNumberCorrect(phoneNumber) &&
    address.length > 10 &&
    !addressError &&
    !!companyRepresentativeName &&
    transactionId &&
    isGoodPassword &&
    password &&
    password === repeatedPassword &&
    hasAgreedTerm

  // 자동 포커스 이동
  useEffect(() => {
    switch (currentStep) {
      case Step.InputRegistrationNumber:
        setCurrentFocus("registrationNumber")
        inputRegistrationNumberRef.current?.focus()
        return
      case Step.InputCustomCompanyName:
        setCurrentFocus("companyName")
        inputCompanyNameRef.current?.focus()
        return
      case Step.InputServiceName:
        setCurrentFocus("serviceName")
        inputNameRef.current?.focus()
        return
      case Step.CompanyRepresentativeName:
        setCurrentFocus("companyRepresentativeName")
        inputCompanyRepresentativeNameRef.current?.focus()
        return
      case Step.InputPhoneNumber:
        setCurrentFocus("phone")
        inputPhoneRef.current?.focus()
        return
      case Step.InputPhoneNumberVerificationKey:
        setCurrentFocus("verificationCode")
        inputVerificationCodeRef.current?.focus()
        return
      case Step.InputPassword:
        setCurrentFocus("password")
        inputPasswordRef.current?.focus()
        return
      case Step.RepeatPassword:
        setCurrentFocus("repeatingPassword")
        inputRepeatPasswordRef.current?.focus()
        return
      case Step.BeforeReadingTerm:
        setCurrentFocus("readTerm")
        buttonReadTermRef.current?.focus()
        return
      case Step.AllDone:
        setCurrentFocus("register")
        buttonRegisterRef.current?.focus()
        return
    }
  }, [currentStep])

  useEffect(() => {
    if (formValidation) {
      setCurrentStep(Step.AllDone)
    }
  }, [formValidation])

  const [registerProcess, setRegisterProcess] = useState<
    "isRegistering" | "registeredSuccessfully"
  >()

  // 휴대전화 인증
  const verifyPhoneNumber = useCallback((phoneNumber: string) => {
    if (!isPhoneNumberCorrect(phoneNumber)) {
      return
    }
    setCurrentStep(Step.SendingPhoneNumberVerificationKey)
    ;(async () => {
      await fetcher("/api/register/send-phone-verification-code", {
        phoneNumber,
      })
      setCurrentStep(Step.InputPhoneNumberVerificationKey)
    })()
  }, [])

  const register = useCallback(async () => {
    if (!formValidation) {
      return
    }
    setRegisterProcess("isRegistering")
    try {
      sendEvent("register_account", "start_registration", registrationNumber)
      await fetcher("/api/register", {
        transactionId,
        registrationNumber,
        phoneNumber,
        password,
        companyName,
        serviceName,
        address,
        companyRepresentativeName,
      })
      sendEvent("register_account", "registration_success", registrationNumber)
      setRegisterProcess("registeredSuccessfully")
    } catch {
      alert("생성 중 에러가 발생했습니다. 잠시 후 다시 시도해주세요.")
      sendEvent("register_account", "registration_failed", registrationNumber)
      setRegisterProcess(undefined)
    }
  }, [
    registrationNumber,
    phoneNumber,
    transactionId,
    password,
    formValidation,
    address,
    companyRepresentativeName,
    serviceName,
  ])

  const formChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { id, value } = e.target

    if (id === "phoneNumber") {
      setCurrentStep(Step.InputPhoneNumber)
      setPhoneNumber(value.replace(/\D/g, "").trim())
      return
    }

    if (id === "registrationNumber") {
      setCurrentStep(Step.InputRegistrationNumber)
      setRegistrationNumber(value.replace(/\D/g, "").trim())
      return
    }

    if (id === "companyName") {
      setCurrentStep(Step.InputCustomCompanyName)
      setCompanyName(value)
      return
    }

    if (id === "companyRepresentativeName") {
      setCompanyRepresentativeName(value)
      return
    }

    if (id === "serviceName") {
      setServiceName(value)
      return
    }

    if (id === "address") {
      setAddress(value)
      return
    }

    if (id === "verificationCode") {
      const verificationCode = value.replace(/\D/g, "").slice(0, 6)
      setVerificationCode(verificationCode)
      if (verificationCode.length === 6) {
        checkVerificationCode(verificationCode)
      }
      return
    }

    if (id === "password") {
      setPassword(value)
      const { isGoodPassword: changeCheck } = checkPassword(value)
      if (
        changeCheck &&
        repeatedPassword === value &&
        currentStep !== Step.BeforeReadingTerm
      ) {
        setCurrentStep(Step.BeforeReadingTerm)
      }
      return
    }

    if (id === "repeatedPassword") {
      setRepeatedPassword(value)
      const { isGoodPassword: changeCheck } = checkPassword(value)
      if (
        changeCheck &&
        password === value &&
        currentStep !== Step.BeforeReadingTerm
      ) {
        setCurrentStep(Step.BeforeReadingTerm)
      }
      return
    }
  }

  const [currentFocus, setCurrentFocus] =
    useState<FocusItem>("registrationNumber")

  // UI
  const inputOverrides = useCallback(
    (focus: FocusItem): InputOverrides => ({
      Root: {
        style:
          currentFocus === focus
            ? {
                borderTopColor: "#000",
                borderRightColor: "#000",
                borderBottomColor: "#000",
                borderLeftColor: "#000",
              }
            : {},
      },
      Input: {
        style: ({ disabled }) =>
          disabled
            ? {
                color: "#888",
                "-webkitTextFillColor": "#888",
                textFillColor: "#888",
              }
            : {},
        ...(focus === "password"
          ? {
              props: {
                minLength: "10",
                maxLength: "32",
                passwordrules:
                  "required: digit; required: upper,lower; allowed: [!#$%&'()*+,./:;<=>?@\\^_`{|}~];",
              },
            }
          : {}),
      },
    }),
    [currentFocus]
  )

  return {
    // State 값들
    currentStep,
    registrationNumber,
    companyName,
    companyNameStatus,
    invalidRegistrationNumberErrorMessage,
    transactionId,
    verificationCode,
    hasPhoneNumberVerified,
    verificationCodeErrorMessage,
    password,
    hasPasswordChecked,
    repeatedPassword,
    hasRepeatedPasswordChecked,
    showPasswordsMismatched,
    hasAgreedTerm,
    hasReachedEndOfTerm,
    phoneNumber,
    registerProcess,
    currentFocus,
    companyRepresentativeName,
    address,
    addressError,
    serviceName,

    // 변수
    formValidation,
    formattedRegisterNumber,
    formattedPhoneNumber,
    isGoodPassword,
    passwordErrorMessage,

    // Ref 값들
    companyNameCacheRef,
    scrollTermRef,
    inputCompanyNameRef,
    inputPhoneRef,
    inputRegistrationNumberRef,
    inputVerificationCodeRef,
    inputPasswordRef,
    inputRepeatPasswordRef,
    buttonReadTermRef,
    buttonRegisterRef,
    inputCompanyRepresentativeNameRef,
    inputAddressRef,
    inputNameRef,

    // 함수
    formChange,
    verifyPhoneNumber,
    setCurrentStep,
    inputOverrides,
    setCurrentFocus,
    setHasPasswordChecked,
    setHasRepeatedPasswordChecked,
    setShowPasswordsMismatched,
    sendEvent,
    setHasAgreedTerm,
    register,
    setCompanyNameStatus,
    checkAddress,
  }
}

export default useSignup

function getCompanyErrorMessage(errorCode: SearchCompanyErrorCode) {
  switch (errorCode) {
    case "ALREADY_REGISTERED":
      return "이미 가입된 회사입니다. 비밀번호를 잊으셨나요? 상단 메뉴의 비밀번호 변경 버튼을 클릭해주세요."
    case "INVALID_REGISTRATION_NUMBER":
      return "존재하지 않는 사업자등록번호입니다."
    case "CLOSED":
      return "폐업한 사업자는 가입이 불가능합니다."
    case "SUSPENDED":
      return "휴업 중인 사업자는 가입이 불가능합니다."
    case "FAIL_TO_GET_COMPANY_NAME":
    case "INTERNAL_ERROR":
      return "에러가 발생하였습니다. 잠시 후 다시 시도해주세요."
  }
}
